import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Save, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Panel administratora | SyncTech" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPage,
});

type Row = {
  id: string;
  slug: string;
  label: string;
  sort_order: number;
  active: boolean;
  // numeric fields (one of):
  base?: number;
  multiplier?: number;
  cost?: number;
};

type TableKey = "pricing_platforms" | "pricing_ranges" | "pricing_integrations";

const TABLES: { key: TableKey; title: string; numField: "base" | "multiplier" | "cost"; numLabel: string; step: string }[] = [
  { key: "pricing_platforms", title: "Platformy (kwota bazowa)", numField: "base", numLabel: "Bazowa cena (zł)", step: "100" },
  { key: "pricing_ranges", title: "Skale zamówień (mnożnik)", numField: "multiplier", numLabel: "Mnożnik", step: "0.05" },
  { key: "pricing_integrations", title: "Integracje (koszt)", numField: "cost", numLabel: "Koszt (zł)", step: "100" },
];

function AdminPage() {
  const [session, setSession] = useState<unknown>(null);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s?.user) checkAdmin(s.user.id);
      else {
        setIsAdmin(false);
        setChecking(false);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) checkAdmin(data.session.user.id);
      else setChecking(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function checkAdmin(uid: string) {
    setChecking(true);
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid).eq("role", "admin").maybeSingle();
    setIsAdmin(!!data);
    setChecking(false);
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) return <LoginForm />;
  if (!isAdmin) return <NoAccess />;
  return <AdminPanel />;
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Błąd logowania");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Toaster />
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur">
        <h1 className="font-display text-2xl font-bold">Panel administratora</h1>
        <p className="text-sm text-muted-foreground">Zaloguj się, aby zarządzać kalkulatorem.</p>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label>Hasło</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zaloguj"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Konta administratorów są tworzone wyłącznie przez właściciela serwisu.
        </p>
      </form>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground">Twoje konto nie ma uprawnień administratora.</p>
      <p className="text-xs text-muted-foreground max-w-md text-center">
        Aby nadać pierwszą rolę admina, otwórz Lovable Cloud → tabela <code>user_roles</code> i dodaj wiersz: user_id (skopiuj z auth.users) + role = "admin".
      </p>
      <Button variant="outline" onClick={() => supabase.auth.signOut()}>
        <LogOut className="mr-2 h-4 w-4" /> Wyloguj
      </Button>
    </div>
  );
}

function AdminPanel() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      <header className="border-b border-border/60 bg-card/40 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <h1 className="font-display text-xl font-bold">Panel kalkulatora</h1>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <a href="/kalkulator" target="_blank" rel="noreferrer">Podgląd kalkulatora ↗</a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => supabase.auth.signOut()}>
              <LogOut className="mr-2 h-4 w-4" /> Wyloguj
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-12">
        {TABLES.map((t) => (
          <TableEditorInner key={t.key} title={t.title} numField={t.numField} numLabel={t.numLabel} step={t.step} />
        ))}
      </main>
    </div>
  );
}

function TableEditorInner({ title, numField, numLabel, step }: { title: string; numField: "base" | "multiplier" | "cost"; numLabel: string; step: string }) {
  const tableKey: TableKey =
    numField === "base" ? "pricing_platforms" : numField === "multiplier" ? "pricing_ranges" : "pricing_integrations";

  // Loose typing — Supabase types schema strictly; admin CRUD is dynamic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await db.from(tableKey).select("*").order("sort_order");
    if (error) toast.error(error.message);
    else setRows((data ?? []) as Row[]);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(id: string, updates: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  }

  async function save(row: Row) {
    setSavingId(row.id);
    const payload: Record<string, unknown> = {
      slug: row.slug.trim(),
      label: row.label.trim(),
      sort_order: Number(row.sort_order) || 0,
      active: row.active,
      [numField]: Number(row[numField] ?? 0),
    };
    const { error } = await db.from(tableKey).update(payload).eq("id", row.id);
    setSavingId(null);
    if (error) toast.error(error.message);
    else toast.success("Zapisano");
  }

  async function remove(row: Row) {
    if (!confirm(`Usunąć "${row.label}"?`)) return;
    const { error } = await db.from(tableKey).delete().eq("id", row.id);
    if (error) return toast.error(error.message);
    setRows((p) => p.filter((r) => r.id !== row.id));
    toast.success("Usunięto");
  }

  async function add() {
    const slug = prompt("Slug (unikalny identyfikator, np. shopify):")?.trim();
    if (!slug) return;
    const label = prompt("Nazwa wyświetlana:")?.trim();
    if (!label) return;
    const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0);
    const insert: Record<string, unknown> = {
      slug,
      label,
      sort_order: maxOrder + 10,
      active: true,
      [numField]: 0,
    };
    const { data, error } = await db.from(tableKey).insert(insert).select().single();
    if (error) return toast.error(error.message);
    setRows((p) => [...p, data as Row]);
    toast.success("Dodano");
  }



  return (
    <section className="rounded-3xl border border-border/60 bg-card/40 p-6 backdrop-blur">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">{title}</h2>
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="mr-1.5 h-4 w-4" /> Dodaj
        </Button>
      </div>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left py-2 pr-3">Slug</th>
                <th className="text-left py-2 pr-3">Nazwa</th>
                <th className="text-left py-2 pr-3">{numLabel}</th>
                <th className="text-left py-2 pr-3">Kolejność</th>
                <th className="text-left py-2 pr-3">Aktywne</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="py-2 pr-3"><Input value={r.slug} onChange={(e) => patch(r.id, { slug: e.target.value })} className="h-8" /></td>
                  <td className="py-2 pr-3"><Input value={r.label} onChange={(e) => patch(r.id, { label: e.target.value })} className="h-8 min-w-[200px]" /></td>
                  <td className="py-2 pr-3"><Input type="number" step={step} value={String(r[numField] ?? 0)} onChange={(e) => patch(r.id, { [numField]: Number(e.target.value) } as Partial<Row>)} className="h-8 w-28" /></td>
                  <td className="py-2 pr-3"><Input type="number" value={r.sort_order} onChange={(e) => patch(r.id, { sort_order: Number(e.target.value) })} className="h-8 w-20" /></td>
                  <td className="py-2 pr-3">
                    <input type="checkbox" checked={r.active} onChange={(e) => patch(r.id, { active: e.target.checked })} className="h-4 w-4" />
                  </td>
                  <td className="py-2 flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => save(r)} disabled={savingId === r.id}>
                      {savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
